/**
 * Deterministic "Back to Platform" navigation for FundGenius HQ.
 *
 * Never uses browser history. Always lands on /platform/dashboard without
 * agency_id query params. Clears persisted FundGenius workspace state so
 * platform owners are not bounced back to clients.
 */

import { FUNDING_WORKSPACE_AGENCY_STORAGE_KEY } from './funding/constants';
import { FUNDELITE_PLATFORM_LANDING, isFundGeniusPath } from './portalLandingRoute';

export const BACK_TO_PLATFORM_PATH = FUNDELITE_PLATFORM_LANDING; // /platform/dashboard
export const BACK_TO_PLATFORM_GUARD_KEY = 'fundelite_back_to_platform_guard';

/** Persisted keys that can rehydrate FundGenius workspace / redirect state. */
export const FUNDGENIUS_WORKSPACE_STORAGE_KEYS = [
  FUNDING_WORKSPACE_AGENCY_STORAGE_KEY, // fundgenius_hq_agency_id
  'redirectTo',
  'returnTo',
  'postLoginRedirect',
  'redirect_to',
  'post_login_redirect',
  'auth_redirect',
  'authRedirect',
  'previousRoute',
  'lastVisitedRoute',
  'selectedWorkspace',
  'selectedAgency',
  'selectedClient',
  'selectedFundingWorkspace',
  'fundingWorkspaceAgencyId',
  'funding-clients-sample-mode',
];

const GUARD_TTL_MS = 5 * 60 * 1000;

function safeRemove(store, key) {
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    // ignore quota / privacy mode errors
  }
}

function storageLooksLikeFundGeniusWorkspace(value) {
  const raw = String(value || '').toLowerCase();
  if (!raw) return false;
  return (
    raw.includes('fundgenius')
    || raw.includes('agency_fundgenius')
    || raw.includes('/platform/fundgenius-hq')
    || raw.includes('agency_id=')
  );
}

/**
 * Clear selected FundGenius agency + remembered workspace / return routes.
 */
export function clearFundGeniusWorkspaceState() {
  if (typeof window === 'undefined') return true;

  const stores = [window.sessionStorage, window.localStorage].filter(Boolean);

  for (const store of stores) {
    for (const key of FUNDGENIUS_WORKSPACE_STORAGE_KEYS) {
      safeRemove(store, key);
    }

    // Sweep any additional keys whose values point at FundGenius workspace routes.
    try {
      const toRemove = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (!key || FUNDGENIUS_WORKSPACE_STORAGE_KEYS.includes(key)) continue;
        const value = store.getItem(key);
        if (storageLooksLikeFundGeniusWorkspace(value) || storageLooksLikeFundGeniusWorkspace(key)) {
          // Never clear auth tokens.
          if (key.includes('token') || key.includes('fundelite_token')) continue;
          toRemove.push(key);
        }
      }
      toRemove.forEach((key) => safeRemove(store, key));
    } catch {
      // ignore enumeration errors
    }
  }

  return true;
}

export function markBackToPlatformGuard(fromPath = '') {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      BACK_TO_PLATFORM_GUARD_KEY,
      JSON.stringify({ at: Date.now(), from: String(fromPath || '') })
    );
  } catch {
    // ignore
  }
}

export function clearBackToPlatformGuard() {
  if (typeof window === 'undefined') return;
  safeRemove(window.sessionStorage, BACK_TO_PLATFORM_GUARD_KEY);
}

export function readBackToPlatformGuard() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(BACK_TO_PLATFORM_GUARD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - Number(parsed.at) > GUARD_TTL_MS) {
      safeRemove(window.sessionStorage, BACK_TO_PLATFORM_GUARD_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function isPlatformDashboardPath(pathname = '') {
  const path = String(pathname || '').split('?')[0].split('#')[0];
  return path === BACK_TO_PLATFORM_PATH || path === `${BACK_TO_PLATFORM_PATH}/`;
}

export function isFundGeniusClientsCandidate(candidate = '') {
  const raw = String(candidate || '');
  if (!raw) return false;
  if (isFundGeniusPath(raw)) return true;
  return (
    raw.startsWith('/platform/fundgenius-hq/clients')
    || raw.includes('/platform/fundgenius-hq/clients?')
  );
}

/**
 * True when a redirect toward FundGenius clients must be rejected after
 * Back to Platform / while already on platform dashboard.
 */
export function shouldRejectStaleFundGeniusRedirect(candidate, currentPathname = '') {
  if (!isFundGeniusClientsCandidate(candidate)) return false;
  if (isPlatformDashboardPath(currentPathname)) return true;
  return Boolean(readBackToPlatformGuard());
}

export function logBackToPlatform({ hostname, from, to, clearedWorkspaceState }) {
  console.info(
    `[BackToPlatform] hostname=${hostname || ''} from=${from || ''} to=${to || ''} cleared_workspace_state=${Boolean(clearedWorkspaceState)}`
  );
}

export function logPortalRedirectRejected({ candidate, reason }) {
  console.info(
    `[PortalRedirectRejected] candidate=${candidate || ''} reason=${reason || ''}`
  );
}

/**
 * Clear workspace state and navigate to /platform/dashboard with replace.
 * Does not use history.back / navigate(-1).
 */
export async function navigateBackToPlatform({
  navigate,
  exitSupportMode,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  from = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
} = {}) {
  const clearedWorkspaceState = clearFundGeniusWorkspaceState();
  markBackToPlatformGuard(from);

  if (typeof exitSupportMode === 'function') {
    try {
      await exitSupportMode();
    } catch (err) {
      console.warn('[BackToPlatform] exitSupportMode failed', err);
    }
  }

  logBackToPlatform({
    hostname,
    from,
    to: BACK_TO_PLATFORM_PATH,
    clearedWorkspaceState,
  });

  if (typeof navigate !== 'function') {
    throw new Error('navigateBackToPlatform requires a navigate function');
  }

  navigate(BACK_TO_PLATFORM_PATH, { replace: true });
  return BACK_TO_PLATFORM_PATH;
}

export default {
  BACK_TO_PLATFORM_PATH,
  clearFundGeniusWorkspaceState,
  navigateBackToPlatform,
  shouldRejectStaleFundGeniusRedirect,
  markBackToPlatformGuard,
  readBackToPlatformGuard,
};
